import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useAuth } from './auth.context';
import { UserProfile, Fixture, NewsItem, CalendarEvent, Player } from '../types';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { createAuthenticatedRequest, handleApiResponse } from './api';

/**
 * Parse player height from various formats to centimeters
 * Handles:
 * - Decimal meters (1.75m, 1,75m) -> 175cm
 * - Integer centimeters (175cm) -> 175cm
 * - Decimal meters without unit (1.75, 1,75) -> 175cm
 * - Integer centimeters without unit (175) -> 175cm
 * - String values with various formats
 */
const parsePlayerHeight = (player: any): number => {
  // If we have a valid height_float, use it (convert from m to cm)
  if (player.height_float && typeof player.height_float === 'number' && player.height_float > 0) {
    return Math.round(player.height_float * 100);
  }
  
  // Try to parse from height string if available
  if (player.height && typeof player.height === 'string') {
    // Remove any non-numeric characters except for decimal separators
    const heightStr = player.height.replace(/[^\d.,]/g, '');
    
    // Replace comma with period for decimal parsing
    const normalizedHeight = heightStr.replace(',', '.');
    
    // Parse the numeric value
    const heightValue = parseFloat(normalizedHeight);
    
    if (!isNaN(heightValue)) {
      // If the value is small (less than 3), assume it's in meters
      if (heightValue < 3) {
        return Math.round(heightValue * 100);
      }
      // Otherwise assume it's already in centimeters
      return Math.round(heightValue);
    }
  }
  
  // If we have a height value as a number directly
  if (player.height && typeof player.height === 'number' && player.height > 0) {
    // If the value is small (less than 3), assume it's in meters
    if (player.height < 3) {
      return Math.round(player.height * 100);
    }
    // Otherwise assume it's already in centimeters
    return Math.round(player.height);
  }
  
  // Return null if no valid height found (will display as '-' in the UI)
  return 0;
};

interface ClubData {
  userProfile: UserProfile | null;
  clubId: string | null;
  clubNews: NewsItem[];
  nextFixture: Fixture | null;
  oppositionId: string | null;
  oppositionNews: NewsItem[];
  calendarEvents: CalendarEvent[];
  teamPlayers: Player[];
  isLoading: boolean;
  error: Error | null;
  refetchAll: () => Promise<void>;
  updateProfile: (clubName: string) => Promise<UserProfile>;
}

const ClubDataContext = createContext<ClubData | undefined>(undefined);

export const ClubDataProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { user, getToken, isAuthenticated } = useAuth();
  const queryClient = useQueryClient();
  
  // Fetch user profile
  const profileQuery = useQuery({
    queryKey: ['userProfile'],
    queryFn: async () => {
      if (!isAuthenticated || !user) {
        return null;
      }
      
      const token = getToken();
      const baseUrl = import.meta.env.DEV ? '' : import.meta.env.VITE_API_URL;
      const res = await fetch(`${baseUrl}/ai/v1/profile/me`, {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        credentials: 'include'
      });
      
      if (!res.ok) {
        throw new Error(`Error: ${res.status} - ${res.statusText}`);
      }
      
      const response = await res.json();
      
      return {
        id: response.user_id,
        userId: response.user_id,
        clubName: response.club_name,
        createdAt: response.created_at,
        updatedAt: response.updated_at
      };
    },
    enabled: isAuthenticated && !!user,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
  
  // Fetch club ID based on profile
  const clubIdQuery = useQuery({
    queryKey: ['clubId', profileQuery.data?.clubName],
    queryFn: async () => {
      if (!profileQuery.data?.clubName) {
        return null;
      }
      
      const token = getToken();
      const requestOptions = createAuthenticatedRequest(token);
      const baseUrl = import.meta.env.DEV ? '' : import.meta.env.VITE_API_URL;
      
      const searchResponse = await fetch(
        `${baseUrl}/ai/v1/club/lookup?name=${encodeURIComponent(profileQuery.data.clubName)}`, 
        requestOptions
      );
      
      if (!searchResponse.ok) {
        throw new Error(`Club lookup failed: ${searchResponse.status}`);
      }
      
      const clubData = await searchResponse.json();
      
      if (!clubData || !clubData.id) {
        throw new Error('Club ID not found');
      }
      
      return clubData.id;
    },
    enabled: !!profileQuery.data?.clubName,
    staleTime: 60 * 60 * 1000, // 1 hour
  });
  
  // Fetch club news
  const clubNewsQuery = useQuery({
    queryKey: ['clubNews', clubIdQuery.data],
    queryFn: async () => {
      if (!clubIdQuery.data) {
        return [];
      }
      
      const token = getToken();
      const baseUrl = import.meta.env.DEV ? '' : import.meta.env.VITE_API_URL;
      const url = `${baseUrl}/ai/v1/club/${clubIdQuery.data}/news`;
      
      const response = await fetch(url, {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        credentials: 'include'
      });
      
      if (!response.ok) {
        throw new Error(`Error fetching news: ${response.status} ${response.statusText}`);
      }
      
      return await response.json();
    },
    enabled: !!clubIdQuery.data,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
  
  // Fetch fixtures (for calendar and next fixture)
  const fixturesQuery = useQuery({
    queryKey: ['fixtures', clubIdQuery.data],
    queryFn: async () => {
      if (!clubIdQuery.data) {
        return [];
      }
      
      const token = getToken();
      const requestOptions = createAuthenticatedRequest(token);
      const baseUrl = import.meta.env.DEV ? '' : import.meta.env.VITE_API_URL;
      
      const fixturesResponse = await fetch(
        `${baseUrl}/ai/v1/club/${clubIdQuery.data}/fixtures?limit=6`, 
        requestOptions
      );
      
      return await handleApiResponse<Fixture[]>(fixturesResponse);
    },
    enabled: !!clubIdQuery.data,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
  
  // Derive next fixture from fixtures
  const nextFixture = fixturesQuery.data && fixturesQuery.data.length > 0 
    ? fixturesQuery.data[0] 
    : null;
  
  // Determine opposition ID from next fixture
  const oppositionId = nextFixture 
    ? (nextFixture.fixture_type === 'home' ? nextFixture.away_team_id : nextFixture.home_team_id)
    : null;
  
  // Fetch opposition news
  const oppositionNewsQuery = useQuery({
    queryKey: ['oppositionNews', oppositionId],
    queryFn: async () => {
      if (!oppositionId) {
        return [];
      }
      
      const token = getToken();
      const requestOptions = createAuthenticatedRequest(token);
      const baseUrl = import.meta.env.DEV ? '' : import.meta.env.VITE_API_URL;
      
      const response = await fetch(
        `${baseUrl}/ai/v1/club/${oppositionId}/news`, 
        requestOptions
      );

      if (!response.ok) {
        throw new Error(`Failed to fetch opposition news: ${response.status}`);
      }

      const newsData = await response.json();
      
      // Transform the API response to match our UI expectations
      return newsData.map((item: any) => ({
        id: item.id,
        headline: item.headline,
        sourceUrl: item.article_url,
        summary: item.snippet || 'No summary available',
        surfacedAt: item.publication_date || item.fetched_at
      }));
    },
    enabled: !!oppositionId,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
  
  // Fetch team players
  const teamPlayersQuery = useQuery({
    queryKey: ['teamPlayers'],
    queryFn: async () => {
      const token = getToken();
      if (!token) {
        throw new Error('Not authenticated');
      }

      const baseUrl = import.meta.env.DEV ? '' : import.meta.env.VITE_API_URL;
      const response = await fetch(`${baseUrl}/ai/v1/club/my-club/players`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        credentials: 'include'
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch team data: ${response.status}`);
      }

      const players = await response.json();
      
      // Transform the response to match the expected format
      return players.map((player: any) => ({
        id: player.id,
        name: player.name,
        photoUrl: player.image_url || '/footballer.png',
        number: player.shirt_number ? parseInt(player.shirt_number.toString().replace(/\D/g, ''), 10) : 0,
        bio: {
          nationality: player.nationality || 'Unknown',
          age: player.age || 0,
          position: player.position || 'Unknown'
        },
        stats: {
          physicalAttributes: {
            height: parsePlayerHeight(player),
            weight: 0,
            preferredFoot: player.foot || 'right'
          }
        }
      }));
    },
    enabled: isAuthenticated,
    staleTime: 10 * 60 * 1000, // 10 minutes
  });
  
  // Convert fixtures to calendar events
  const calendarEvents: CalendarEvent[] = fixturesQuery.data 
    ? fixturesQuery.data.map(fixture => {
        const isHome = fixture.fixture_type === 'home';
        const opponent = isHome ? fixture.away_team_name : fixture.home_team_name;
        const location = isHome ? 'Home' : 'Away';
        
        return {
          id: fixture.id,
          date: fixture.scheduled,
          description: `${location} vs ${opponent}`
        };
      })
    : [];
  
  // Function to update user profile
  const updateProfile = async (clubName: string): Promise<UserProfile> => {
    if (!isAuthenticated || !user) {
      throw new Error('User must be authenticated to update profile');
    }

    const token = getToken();
    const baseUrl = import.meta.env.DEV ? '' : import.meta.env.VITE_API_URL;
    const res = await fetch(`${baseUrl}/ai/v1/profile/me`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify({ club_name: clubName }),
      credentials: 'include'
    });
    
    if (!res.ok) {
      throw new Error(`Error: ${res.status} - ${res.statusText}`);
    }
    
    const response = await res.json();
    
    const updatedProfile = {
      id: response.user_id,
      userId: response.user_id,
      clubName: response.club_name,
      createdAt: response.created_at,
      updatedAt: response.updated_at
    };
    
    // Update the cache with the new profile
    queryClient.setQueryData(['userProfile'], updatedProfile);
    
    // Invalidate dependent queries
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['clubId'] }),
      queryClient.invalidateQueries({ queryKey: ['clubNews'] }),
      queryClient.invalidateQueries({ queryKey: ['fixtures'] }),
      queryClient.invalidateQueries({ queryKey: ['oppositionNews'] }),
      queryClient.invalidateQueries({ queryKey: ['teamPlayers'] }),
    ]);
    
    return updatedProfile;
  };
  
  // Function to refetch all data
  const refetchAll = async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['userProfile'] }),
      queryClient.invalidateQueries({ queryKey: ['clubId'] }),
      queryClient.invalidateQueries({ queryKey: ['clubNews'] }),
      queryClient.invalidateQueries({ queryKey: ['fixtures'] }),
      queryClient.invalidateQueries({ queryKey: ['oppositionNews'] }),
      queryClient.invalidateQueries({ queryKey: ['teamPlayers'] }),
    ]);
  };
  
  // Determine overall loading state
  const isLoading = 
    profileQuery.isLoading || 
    clubIdQuery.isLoading || 
    clubNewsQuery.isLoading || 
    fixturesQuery.isLoading || 
    oppositionNewsQuery.isLoading ||
    teamPlayersQuery.isLoading;
  
  // Determine overall error state
  const error = 
    profileQuery.error || 
    clubIdQuery.error || 
    clubNewsQuery.error || 
    fixturesQuery.error || 
    oppositionNewsQuery.error ||
    teamPlayersQuery.error || 
    null;
  
  const value = {
    userProfile: profileQuery.data,
    clubId: clubIdQuery.data,
    clubNews: clubNewsQuery.data || [],
    nextFixture,
    oppositionId,
    oppositionNews: oppositionNewsQuery.data || [],
    calendarEvents,
    teamPlayers: teamPlayersQuery.data || [],
    isLoading,
    error,
    refetchAll,
    updateProfile,
  };
  
  return (
    <ClubDataContext.Provider value={value}>
      {children}
    </ClubDataContext.Provider>
  );
};

export const useClubData = () => {
  const context = useContext(ClubDataContext);
  if (context === undefined) {
    throw new Error('useClubData must be used within a ClubDataProvider');
  }
  return context;
};